import { Outlet } from "react-router-dom"
import { Layers } from "lucide-react"

import { SeccionTabs } from "@/components/SeccionTabs"
import { Toolbar, ToolbarField, ToolbarNote } from "@/components/Toolbar"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { t } from "@/lib/i18n"

import {
  ANIOS_DISPONIBLES,
  FinanceFiltersProvider,
  useFinanceFilters,
} from "@/routes/finance/context"

/**
 * LA GESTIÓN DEL GRUPO: las vistas de Finanzas, con las cuatro sumadas.
 *
 * Cesar: «en holding deberíamos tener finanzas y contabilidad sumadas
 * (deberíamos tener las mismas vistas: P&L, flujo de caja, cobranzas, payroll,
 * balance general, estado de resultados, etc.)».
 *
 * SE QUITÓ EL 2026-10-01 Y VOLVIÓ EL 2026-10-04. Se quitó porque era la misma
 * consulta que Finanzas con «Todas las sociedades». Cesar la pidió de vuelta,
 * y con el forecast: «en holding deberíamos de poder tener forecast también en
 * base a la suma de las sociedades. Igual debería de poder ver P&L y Flujo de
 * caja exactamente igual que en finanzas pero con todo sumado». Que el número
 * sea el mismo que en Finanzas no es un defecto: es la garantía. Lo que aporta
 * es el SITIO —mirar el grupo sin pasar por el selector de sociedad— y el
 * forecast sumado, que en Finanzas exige elegir una sociedad para ajustarse.
 *
 * ===========================================================================
 * QUÉ FALTABA DE VERDAD, Y QUÉ YA ESTABA
 * ===========================================================================
 *
 * Lo CONTABLE ya estaba: el **estado de resultados consolidado** y el **balance
 * general consolidado** los calcula el motor de consolidación y los pinta la
 * pestaña Consolidado. Lo que pasaba es lo mismo que en Contabilidad con
 * «Estados contables»: existían y no se encontraban, porque estaban dentro de
 * otra pestaña sin nada que los anunciara. Eso se arregla nombrándolos, no
 * construyéndolos otra vez — y duplicarlos daría dos consolidados que algún día
 * dejarían de coincidir, delante de un directorio.
 *
 * Lo que NO estaba es la mitad de GESTIÓN: el P&L del modelo, la caja y la
 * cobranza del grupo. Eso es esto.
 *
 * ===========================================================================
 * SON LAS MISMAS PANTALLAS, NO UNAS PARECIDAS
 * ===========================================================================
 *
 * `PnlPage`, `CashFlowPage` y `CobranzasPage` se montan TAL CUAL. Lo único que
 * cambia es el alcance del proveedor de filtros: `grupo` en vez de `sociedad`,
 * y con eso el país deja de viajar y el backend devuelve el consolidado (ver
 * `_filtro_pais`).
 *
 * Copiarlas habría sido más rápido y es justo lo que no se puede hacer: el P&L
 * del grupo y el de una sociedad tienen que tener la misma línea roja, el mismo
 * orden de cuentas y el mismo botón de descarga, y dos copias divergen en el
 * primer arreglo que alguien haga en una sola.
 *
 * ===========================================================================
 * ESTO NO ES EL CONSOLIDADO CONTABLE, Y HAY QUE DECIRLO
 * ===========================================================================
 *
 * Suma el modelo de gestión de los cuatro países en USD. **No elimina lo que las
 * sociedades se facturan entre ellas** ni aplica las tres tasas de conversión de
 * la NIC 21 — eso es lo que hace el Consolidado, y por eso las dos cifras no
 * coinciden ni deben coincidir.
 *
 * Presentarlas juntas sin decirlo sería la peor versión de esta pantalla: dos
 * números plausibles para «el resultado del grupo» y ninguna pista de cuál
 * contestar cuando alguien pregunte. El aviso va arriba y no en un pie.
 */
export function GestionDelGrupo() {
  return (
    <FinanceFiltersProvider alcance="grupo">
      <div className="space-y-3">
        <SeccionTabs
          ariaLabel="Gestión del grupo"
          tabs={[
            { to: "pnl", label: "P&L del grupo" },
            { to: "caja", label: "Caja del grupo" },
            { to: "forecast", label: "Forecast del grupo" },
            { to: "cobranzas", label: "Cobranzas del grupo" },
          ]}
        />
        <BarraDelGrupo />
        {/*
          EL AVISO, UNA VEZ Y AQUÍ. Si lo pusiera cada pestaña serían tres
          redacciones parecidas de la misma advertencia, y tres advertencias
          parecidas se leen como ninguna.
        */}
        <AvisoDeSuma />
        <Outlet />
      </div>
    </FinanceFiltersProvider>
  )
}

function BarraDelGrupo() {
  const { anio, setAnio } = useFinanceFilters()
  return (
    <Toolbar>
      <ToolbarField label={t("Año")} htmlFor="anio-grupo">
        <Select value={String(anio)} onValueChange={(v) => setAnio(Number(v))}>
          <SelectTrigger id="anio-grupo" className="w-28"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ANIOS_DISPONIBLES.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ToolbarField>

      {/*
        NO HAY SELECTOR DE SOCIEDAD, y su ausencia es el mensaje: aquí están las
        cuatro. En Finanzas ese control existe y manda; aquí sobraría.
      */}
      <ToolbarNote icon={Layers} className="ml-auto">
        {t("Las cuatro sociedades, sumadas en USD")}
      </ToolbarNote>
    </Toolbar>
  )
}

/**
 * El aviso que separa esta suma del consolidado contable.
 *
 * Va como componente y no como texto suelto para que las tres pestañas digan lo
 * MISMO: tres redacciones parecidas de la misma advertencia se leen como tres
 * advertencias distintas, y entonces no se lee ninguna.
 */
function AvisoDeSuma() {
  return (
    <p className="rounded-md border border-warning/40 bg-warning-soft px-2.5 py-1.5 text-2xs leading-5">
      <span className="font-medium">{t("Esto suma, no consolida.")}{" "}</span>
      {t("Son las cuatro sociedades en USD, sin eliminar lo que se facturan entre ellas y sin las tres tasas de la NIC 21. El consolidado contable —el que se firma— está en la pestaña Consolidado, y no tiene por qué dar la misma cifra.")}
    </p>
  )
}
